import React, { useState } from 'react';

import { Plus as AddOutlined, X as CloseOutlined } from "lucide-react";
import { Box, ButtonBase, InputLabel, Menu, Stack } from '@mui/material';

import Picker from './Picker';

const COLOR_SWATCH_CLASS = 'email-builder-color-swatch';

const BUTTON_SX = {
  border: '1px solid',
  borderColor: 'cadet.400',
  width: 32,
  height: 32,
  minWidth: 32,
  minHeight: 32,
  maxWidth: 32,
  maxHeight: 32,
  flexShrink: 0,
  borderRadius: '4px',
  bgcolor: '#FFFFFF',
  boxSizing: 'border-box',
};

type Props =
  | {
      nullable: true;
      label: string;
      onChange: (value: string | null) => void;
      defaultValue: string | null;
    }
  | {
      nullable: false;
      label: string;
      onChange: (value: string) => void;
      defaultValue: string;
    };
export default function ColorInput({ label, defaultValue, onChange, nullable }: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [value, setValue] = useState(defaultValue);
  const handleClickOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const renderResetButton = () => {
    if (!nullable) {
      return null;
    }
    if (typeof value !== 'string' || value.trim().length === 0) {
      return null;
    }
    return (
      <ButtonBase
        className={COLOR_SWATCH_CLASS}
        onClick={() => {
          setValue(null);
          onChange(null);
        }}
      >
        {/* `style` cannot resolve a MUI palette path -- sx can. */}
        <Box component={CloseOutlined} className="size-4" sx={{ color: 'text.secondary' }} />
      </ButtonBase>
    );
  };

  const renderOpenButton = () => {
    if (value) {
      return (
        <ButtonBase
          className={COLOR_SWATCH_CLASS}
          onClick={handleClickOpen}
          sx={{ ...BUTTON_SX, bgcolor: value }}
        />
      );
    }
    return (
      <ButtonBase className={COLOR_SWATCH_CLASS} onClick={handleClickOpen} sx={BUTTON_SX}>
        <AddOutlined className="size-4" />
      </ButtonBase>
    );
  };

  return (
    <Stack alignItems="flex-start">
      <InputLabel sx={{ mb: 0.5 }}>{label}</InputLabel>
      <Stack direction="row" spacing={1}>
        {renderOpenButton()}
        {renderResetButton()}
      </Stack>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{
          sx: { height: 'auto', padding: 0 },
        }}
      >
        <Picker
          value={value || ''}
          onChange={(v) => {
            setValue(v);
            onChange(v);
          }}
        />
      </Menu>
    </Stack>
  );
}
